import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SiteSeverity } from "@safers/api";
import {
  DEVICE_FOCUS_MS,
  filterDevices,
  isUnavailableCctv,
  type ModelDeviceFilter,
  type ModelDevicePoint,
} from "@safers/model/devices";
import {
  ModelDeviceFilters,
  ModelDevicePopover,
  popoverSide,
  type ModelDeviceSelection,
} from "@safers/model/controls";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import {
  createClusterHtml,
  createPoiHtml,
  pinIconBox,
  PIN_ICON_ANCHOR,
  PIN_ICON_SIZE,
} from "./markers";
import { clusterByRegion, clusterByPixel, type Cluster, type RegionAnchors } from "./clustering";
import { regionAnchors } from "./regions";
import { declutterClusters } from "./declutter";
import { MapPopover, type PopoverState } from "./MapPopover";
import { ESRI_IMAGERY, imagerySource } from "./tiles";
import { addTileLayerWithFallback } from "./tileLayer";
import {
  aerialPhotoDates,
  aerialPhotoSource,
  EMPTY_AERIAL_PHOTOS,
  type AerialPhotoCatalog,
} from "./aerialPhotos";
import { AerialPhotoControl } from "./AerialPhotoControl";
import { silhouetteOpacityAt, tileOpacityAt, SITE_Z } from "./zoomFade";

/** 목록에서 장비를 고르면 이 확대 단계까지 들어간다. 위성지도 최대 단계(19)다. */
const DEVICE_FOCUS_ZOOM = SITE_Z + 2;

/** 3D 뷰어 나침반과 같은 바늘. */
const COMPASS_SVG =
  '<svg width="12" height="18" viewBox="0 0 16 22" aria-hidden="true"><path d="M8 0L15 20L8 16Z" fill="#dc2626"/><path d="M8 0L1 20L8 16Z" fill="#94a3b8"/></svg>';
import { removeLeafletMap } from "./removeLeafletMap";
import { createDeviceMarkerLayer, type DeviceMarkerLayer } from "./deviceMarkerLayer";
import {
  createEquipmentBadge,
  updateEquipmentBadge,
  type EquipmentPoint,
} from "@safers/model/equipment";

const SIDO_GEOJSON = "/geojson/sido_no_islands_ver20260201_optimized.geojson";

const JEJU_SIDO = "50";

const JEJU_BOUNDS = { maxLat: 34.0, minLon: 125.5, maxLon: 127.5 };

const LAND_FILL = "#FFFFFF";
const LAND_STROKE = "#A4A9C2";
const LAND_SHADOW = "#3A4A5A";
const SEA_BG = "radial-gradient(ellipse at center, #D8E2ED 0%, #E8EEF5 50%, #F2F5F9 100%)";

const MAP_MAX_ZOOM = 22;
const NO_EQUIPMENT: EquipmentPoint[] = [];
/** 중장비 이름표는 장비 핀 이름표와 같은 단계부터 보인다. 멀리서는 서로 겹친다. */
const EQUIPMENT_LABEL_ZOOM = SITE_Z + 1;

export interface SilhouettePoi {
  id: string;
  lat: number;
  lon: number;
  severity: SiteSeverity;
  label?: string;
  region?: string | null;
}

interface SilhouetteMapProps {
  pois: SilhouettePoi[];
  focus?: { lat: number; lon: number } | null;
  devices?: ModelDevicePoint[];
  deviceFilter?: ModelDeviceFilter;
  onDeviceFilterChange?: (filter: ModelDeviceFilter) => void;
  /** 목록에서 고른 장비. 날아가 팝업을 연 뒤 onFocusHandled 로 바깥에서 지운다. */
  focusDevice?: { deviceId: string } | null;
  onFocusHandled?: () => void;
  deviceNotice?: string;
  onDeviceNavigate?: (path: string) => void;
  /** 중장비 위치. 장비 핀과 다른 각진 배지로 올리고 필터 칩·팝업은 없다. */
  equipment?: EquipmentPoint[];
  aerialPhotos?: AerialPhotoCatalog;
  flyDelayMs?: number;
  onPoiClick?: (id: string) => void;
  selectedId?: string | null;
  className?: string;
}

function isJejuPoi(p: SilhouettePoi): boolean {
  return p.lat < JEJU_BOUNDS.maxLat && p.lon > JEJU_BOUNDS.minLon && p.lon < JEJU_BOUNDS.maxLon;
}

function sidoOf(f: Feature<Geometry>): string {
  return String((f.properties as { sido?: string } | null)?.sido ?? "");
}

function drawSilhouette(
  map: L.Map,
  features: Feature<Geometry>[],
  padding: { topLeft: [number, number]; bottomRight: [number, number] }
): L.LatLngBounds {
  const fc: FeatureCollection<Geometry> = { type: "FeatureCollection", features };
  L.geoJSON(fc, {
    pane: "silhouette-shadow",
    interactive: false,
    style: { fillColor: LAND_SHADOW, fillOpacity: 1, stroke: false },
  }).addTo(map);
  const land = L.geoJSON(fc, {
    pane: "silhouette-land",
    interactive: false,
    style: { fillColor: LAND_FILL, fillOpacity: 1, color: LAND_STROKE, weight: 1 },
  }).addTo(map);

  const bounds = land.getBounds();
  map.fitBounds(bounds, {
    paddingTopLeft: padding.topLeft,
    paddingBottomRight: padding.bottomRight,
  });
  return bounds;
}

const INSET_RESERVE = 142;

const JEJU_PIN_SCALE = 0.85;
const jejuPinBox = pinIconBox(JEJU_PIN_SCALE);
const JEJU_PIN_HEADROOM = jejuPinBox.size[1] + 6;

const JEJU_CLUSTER_PX = jejuPinBox.size[0] + 4;

interface MarkerStyle {
  pinScale?: number;
  pinSize: [number, number];
  pinAnchor: [number, number];
  clusterSize: [number, number];
  clusterAnchor: [number, number];
  /** 클러스터를 눌러 개별 핀으로 펼칠 수 있는지. */
  expandable: boolean;
}

const MAINLAND_MARKERS: MarkerStyle = {
  pinSize: PIN_ICON_SIZE,
  pinAnchor: PIN_ICON_ANCHOR,
  clusterSize: [38, 38],
  clusterAnchor: [19, 19],
  expandable: true,
};

// 인셋은 픽셀 거리로 묶는다. 펼치면 방금 겹쳐서 묶은 자리에 다시 포개지므로
// 펼치지 않고 팝오버 목록으로만 보여준다.
const JEJU_MARKERS: MarkerStyle = {
  pinScale: JEJU_PIN_SCALE,
  pinSize: jejuPinBox.size,
  pinAnchor: jejuPinBox.anchor,
  clusterSize: [32, 32],
  clusterAnchor: [16, 16],
  expandable: false,
};

function preparePanes(map: L.Map, offset: string, blur: string): void {
  const shadow = map.createPane("silhouette-shadow");
  shadow.style.zIndex = "350";
  shadow.style.filter = `blur(${blur})`;
  shadow.style.opacity = "0.6";
  shadow.style.transform = offset;
  const land = map.createPane("silhouette-land");
  land.style.zIndex = "360";
}

export function SilhouetteMap({
  pois,
  focus = null,
  devices,
  deviceFilter,
  onDeviceFilterChange,
  onDeviceNavigate,
  focusDevice,
  onFocusHandled,
  deviceNotice,
  equipment = NO_EQUIPMENT,
  aerialPhotos = EMPTY_AERIAL_PHOTOS,
  flyDelayMs = 0,
  onPoiClick,
  selectedId,
  className = "",
}: SilhouetteMapProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [deviceSelection, setDeviceSelection] = useState<ModelDeviceSelection | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [aerialEnabled, setAerialEnabled] = useState(true);
  const [pickedPhoto, setPickedPhoto] = useState<{ siteKey: string; date: string } | null>(null);
  const photoSiteKey = selectedId ?? null;
  const catalog =
    aerialPhotos.siteId != null && String(aerialPhotos.siteId) === photoSiteKey
      ? aerialPhotos
      : EMPTY_AERIAL_PHOTOS;
  const photoDates = useMemo(() => aerialPhotoDates(catalog.photos), [catalog.photos]);
  const pickedDate = pickedPhoto?.siteKey === photoSiteKey ? pickedPhoto.date : "";
  const selectedPhotoDate = photoDates.includes(pickedDate) ? pickedDate : (photoDates[0] ?? "");
  const aerialSource = useMemo(
    () =>
      aerialEnabled
        ? aerialPhotoSource(catalog.photos.find((item) => item.captureDate === selectedPhotoDate))
        : null,
    [aerialEnabled, selectedPhotoDate, catalog.photos]
  );
  const [size, setSize] = useState({ w: 0, h: 0 });
  const mainElRef = useRef<HTMLDivElement>(null);
  const jejuElRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<L.Map | null>(null);
  const jejuRef = useRef<L.Map | null>(null);
  const clusterRef = useRef<L.LayerGroup | null>(null);
  const jejuLayerRef = useRef<L.LayerGroup | null>(null);
  const onPoiClickRef = useRef(onPoiClick);
  const tileRef = useRef<L.TileLayer | null>(null);
  const nationalBoundsRef = useRef<L.LatLngBounds | null>(null);
  const anchorsRef = useRef<RegionAnchors>({});
  const [anchorsReady, setAnchorsReady] = useState(false);
  const deviceLayerRef = useRef<DeviceMarkerLayer | null>(null);
  const [zoom, setZoom] = useState(0);
  const showInset = zoom > 0 && silhouetteOpacityAt(zoom) > 0.5;

  onPoiClickRef.current = onPoiClick;

  const openPopover = useCallback(
    (map: L.Map, lat: number, lon: number, sites: PopoverState["sites"], clusterKey?: string) => {
      const root = rootRef.current;
      const container = map.getContainer();
      if (!root) return;
      const pt = map.latLngToContainerPoint([lat, lon]);
      const rootBox = root.getBoundingClientRect();
      const mapBox = container.getBoundingClientRect();
      setPopover({
        x: pt.x + (mapBox.left - rootBox.left),
        y: pt.y + (mapBox.top - rootBox.top),
        sites,
        clusterKey,
      });
    },
    []
  );

  const pickFromList = useCallback(
    (siteId: string) => {
      setExpanded(popover?.clusterKey ?? null);
      setPopover(null);
      onPoiClickRef.current?.(siteId);
    },
    [popover?.clusterKey]
  );

  const focusKey = focus ? `${focus.lat},${focus.lon}` : null;
  const focusRef = useRef(focus);
  focusRef.current = focus;

  const openRef = useRef(openPopover);
  openRef.current = openPopover;

  useEffect(() => {
    const mainEl = mainElRef.current;
    const jejuEl = jejuElRef.current;
    if (!mainEl || !jejuEl || mainRef.current) return;

    const main = L.map(mainEl, {
      center: [36.5, 127.8],
      zoom: 7,
      minZoom: 1,
      maxZoom: MAP_MAX_ZOOM,
      zoomControl: false,
      attributionControl: true,
      zoomSnap: 0,
    });
    // 확대·축소 버튼은 오른쪽 아래 — 왼쪽 위는 장비 칩이 쓴다.
    L.control.zoom({ position: "bottomright" }).addTo(main);
    // 나침반 — 2D 지도는 돌지 않아 늘 북쪽이 위다. 3D 와 같은 모양으로 확대·축소 버튼 위에 둔다
    // (아래 모서리 컨트롤은 나중에 단 것이 위에 쌓인다).
    const compass = new L.Control({ position: "bottomright" });
    compass.onAdd = () => {
      const el = L.DomUtil.create("div", "safers-compass");
      el.title = "북쪽이 위";
      el.setAttribute("role", "img");
      el.setAttribute("aria-label", "북쪽이 위");
      el.innerHTML = COMPASS_SVG;
      return el;
    };
    compass.addTo(main);
    main.attributionControl.setPrefix(false);
    // 출처는 타일 이용약관상 빼지 못해 왼쪽 아래에 작게 둔다(3D 와 같은 자리).
    main.attributionControl.setPosition("bottomleft");
    preparePanes(main, "translate(0.5rem, 0.75rem)", "0.625rem");
    mainRef.current = main;

    const tile = addTileLayerWithFallback(main, imagerySource(), ESRI_IMAGERY, {
      maxZoom: MAP_MAX_ZOOM,
      noWrap: true,
      opacity: 0,
    });
    tileRef.current = tile;

    const applyFade = () => {
      const z = main.getZoom();
      setZoom(z);
      tileRef.current?.setOpacity(tileOpacityAt(z));
      const o = String(silhouetteOpacityAt(z));
      const land = main.getPane("silhouette-land");
      const shadow = main.getPane("silhouette-shadow");
      if (land) land.style.opacity = o;
      if (shadow) shadow.style.opacity = String(silhouetteOpacityAt(z) * 0.6);
    };
    main.on("zoom zoomend", applyFade);
    applyFade();

    const jeju = L.map(jejuEl, {
      center: [33.38, 126.55],
      zoom: 8,
      zoomControl: false,
      attributionControl: false,
      zoomSnap: 0,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
    });
    preparePanes(jeju, "translate(0.1875rem, 0.25rem)", "0.25rem");
    jejuRef.current = jeju;

    let cancelled = false;
    fetch(`${import.meta.env.VITE_CONTEXT_PATH ?? ""}${SIDO_GEOJSON}`)
      .then((r) => r.json())
      .then((geo: FeatureCollection<Geometry>) => {
        if (cancelled || !mainRef.current) return;
        anchorsRef.current = regionAnchors(geo.features);
        setAnchorsReady(true);
        const jejuFeatures = geo.features.filter((f) => sidoOf(f) === JEJU_SIDO);
        const mainland = geo.features.filter((f) => sidoOf(f) !== JEJU_SIDO);
        nationalBoundsRef.current = drawSilhouette(main, mainland, {
          topLeft: [8, 8],
          bottomRight: [8, INSET_RESERVE],
        });
        const f = focusRef.current;
        if (f) main.setView([f.lat, f.lon], SITE_Z, { animate: false });
        if (jejuFeatures.length > 0)
          drawSilhouette(jeju, jejuFeatures, {
            topLeft: [10, JEJU_PIN_HEADROOM],
            bottomRight: [10, 8],
          });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      main.off("zoom zoomend");
      tile.remove();
      tile.off();
      tileRef.current = null;
      removeLeafletMap(main);
      removeLeafletMap(jeju);
      mainRef.current = null;
      jejuRef.current = null;
    };
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    if (!main || !aerialSource) return;
    const layer = L.tileLayer(aerialSource.url, {
      attribution: aerialSource.attribution,
      minZoom: aerialSource.minZoom,
      maxZoom: aerialSource.maxZoom,
      maxNativeZoom: aerialSource.maxNativeZoom,
      noWrap: true,
      zIndex: 2,
    });
    layer.on("tileerror", (event: L.TileErrorEvent) => {
      event.tile.style.visibility = "hidden";
    });
    layer.addTo(main);
    return () => {
      layer.remove();
      layer.off();
    };
  }, [aerialSource]);

  useEffect(() => {
    const main = mainRef.current;
    const root = rootRef.current;
    if (!main || !root) return;
    const closePopover = () => setPopover(null);
    const clearSelection = () => {
      closePopover();
      setExpanded(null);
    };
    main.on("movestart zoomstart", closePopover);
    main.on("click", clearSelection);
    const measure = () => setSize({ w: root.clientWidth, h: root.clientHeight });
    const ro = new ResizeObserver(() => {
      measure();
      main.invalidateSize({ animate: false });
      closePopover();
    });
    ro.observe(root);
    measure();
    return () => {
      main.off("movestart zoomstart", closePopover);
      main.off("click", clearSelection);
      ro.disconnect();
    };
  }, [pois]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    setPopover(null);
    const target = focusRef.current;
    const id = window.setTimeout(() => {
      main.invalidateSize({ animate: false });
      if (target) {
        main.flyTo([target.lat, target.lon], SITE_Z, { duration: 1.1 });
        return;
      }
      const bounds = nationalBoundsRef.current;
      if (bounds) {
        main.flyToBounds(bounds, {
          paddingTopLeft: [8, 8],
          paddingBottomRight: [8, INSET_RESERVE],
          duration: 1.1,
        });
      }
    }, flyDelayMs);
    return () => window.clearTimeout(id);
  }, [focusKey, flyDelayMs]);

  const shownDevices = useMemo(
    () => (devices && deviceFilter ? filterDevices(devices, deviceFilter) : devices),
    [devices, deviceFilter]
  );
  const latestDevices = useRef(shownDevices);
  const deviceHandlers = useRef({ onDeviceNavigate, onFocusHandled });
  useEffect(() => {
    latestDevices.current = shownDevices;
  }, [shownDevices]);
  useEffect(() => {
    deviceHandlers.current = { onDeviceNavigate, onFocusHandled };
  }, [onDeviceNavigate, onFocusHandled]);
  /** 목록 장비로 날아가는 중이면 도착 대기를 거두는 함수 */
  const cancelDeviceFlight = useRef<(() => void) | null>(null);

  const selectDevice = useCallback((id: string, anchor: HTMLButtonElement) => {
    const main = mainRef.current;
    const device = latestDevices.current?.find((d) => d.deviceId === id);
    if (!main || !device) return;
    if (device.type === "CCTV") {
      if (!isUnavailableCctv(device) && device.videoPath)
        deviceHandlers.current.onDeviceNavigate?.(device.videoPath);
      return;
    }
    const boundary = main.getContainer();
    setDeviceSelection({ id, anchor, boundary, side: popoverSide(anchor, boundary) });
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    if (!main || !focusKey) return;
    const layer = createDeviceMarkerLayer(main, selectDevice);
    deviceLayerRef.current = layer;
    const closeSelection = () => setDeviceSelection(null);
    main.on("movestart zoomstart", closeSelection);

    return () => {
      main.off("movestart zoomstart", closeSelection);
      cancelDeviceFlight.current?.();
      layer.destroy();
      deviceLayerRef.current = null;
      setDeviceSelection(null);
    };
  }, [focusKey, selectDevice]);

  useEffect(() => {
    const main = mainRef.current;
    const layer = deviceLayerRef.current;
    if (!main || !layer) return;

    const removed = layer.update(shownDevices ?? []);
    if (removed.length)
      setDeviceSelection((current) => (current && removed.includes(current.id) ? null : current));
  }, [shownDevices, focusKey, selectDevice]);

  const showEquipment = focus != null;
  const equipmentLayerRef = useRef<{
    map: L.Map;
    layer: L.LayerGroup;
    markers: Map<string, { marker: L.Marker; badge: HTMLDivElement }>;
  } | null>(null);
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    // 지도가 새로 만들어졌으면(개발 모드의 StrictMode 재마운트 등) 레이어도 새 지도에 다시 만든다.
    const state =
      equipmentLayerRef.current?.map === main
        ? equipmentLayerRef.current
        : { map: main, layer: L.layerGroup().addTo(main), markers: new Map() };
    equipmentLayerRef.current = state;
    const points = showEquipment ? equipment : NO_EQUIPMENT;
    const ids = new Set(points.map((point) => point.id));
    for (const [id, entry] of state.markers) {
      if (ids.has(id)) continue;
      entry.marker.remove();
      state.markers.delete(id);
    }
    for (const point of points) {
      const entry = state.markers.get(point.id);
      if (entry) {
        updateEquipmentBadge(entry.badge, point);
        entry.marker.setLatLng([point.lat, point.lon]);
        continue;
      }
      const badge = createEquipmentBadge(point);
      const marker = L.marker([point.lat, point.lon], {
        icon: L.divIcon({ html: badge, className: "", iconSize: [0, 0] }),
        interactive: false,
        keyboard: false,
      }).addTo(state.layer);
      state.markers.set(point.id, { marker, badge });
    }
  }, [equipment, showEquipment]);

  useEffect(() => {
    // 핀이 지도에 오르면 장비 쪽으로 확대해 날아가고 요청을 지운다.
    const main = mainRef.current;
    const id = focusDevice?.deviceId;
    const target = id ? deviceLayerRef.current?.marker(id) : undefined;
    if (!main || !id || !target) return;
    deviceHandlers.current.onFocusHandled?.();
    cancelDeviceFlight.current?.();
    // 드래그로 비행이 끊겨도 moveend 는 온다 — 장비에 도착했을 때만 팝업을 연다.
    const arrive = () => {
      cancelDeviceFlight.current = null;
      const center = main.latLngToContainerPoint(main.getCenter());
      if (center.distanceTo(main.latLngToContainerPoint(target.latLng)) < 2)
        selectDevice(id, target.button);
    };
    main.once("moveend", arrive);
    cancelDeviceFlight.current = () => main.off("moveend", arrive);
    main.flyTo(target.latLng, Math.max(main.getZoom(), DEVICE_FOCUS_ZOOM), {
      duration: DEVICE_FOCUS_MS / 1000,
    });
  }, [focusDevice, shownDevices, focusKey, selectDevice]);

  useEffect(() => {
    const main = mainRef.current;
    const jeju = jejuRef.current;
    if (!main || !jeju) return;

    const drawClusters = (map: L.Map, clusters: Cluster[], style: MarkerStyle): L.LayerGroup => {
      const layer = L.layerGroup();

      const drawSingle = (p: SilhouettePoi, ownerKey: string, spawnFrom?: L.LatLngExpression) => {
        let spawn = "";
        if (spawnFrom) {
          const a = map.latLngToLayerPoint(spawnFrom);
          const b = map.latLngToLayerPoint([p.lat, p.lon]);
          spawn = `--spawn-x:${Math.round(a.x - b.x)}px;--spawn-y:${Math.round(a.y - b.y)}px;`;
        }
        L.marker([p.lat, p.lon], {
          icon: L.divIcon({
            html: createPoiHtml({
              severity: p.severity,
              selected: p.id === selectedId,
              scale: style.pinScale,
              spawnStyle: spawn || undefined,
            }),
            className: "safers-poi-icon",
            iconSize: style.pinSize,
            iconAnchor: style.pinAnchor,
          }),
          title: p.label,
        })
          .on("click", (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e.originalEvent);
            if (style.expandable) setExpanded(ownerKey);
            setPopover(null);
            onPoiClickRef.current?.(p.id);
          })
          .addTo(layer);
      };

      clusters.forEach((c) => {
        if (c.items.length === 1 || (style.expandable && expanded === c.key)) {
          const from = c.items.length > 1 ? ([c.lat, c.lon] as L.LatLngExpression) : undefined;
          c.items.forEach((p) => drawSingle(p as SilhouettePoi, c.key, from));
          return;
        }
        L.marker([c.lat, c.lon], {
          icon: L.divIcon({
            html: createClusterHtml({ count: c.items.length, severity: c.severity }),
            className: "safers-poi-icon",
            iconSize: style.clusterSize,
            iconAnchor: style.clusterAnchor,
          }),
          title: `현장 ${c.items.length}개`,
        })
          .on("click", (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e.originalEvent);
            if (style.expandable) setExpanded(null);
            openRef.current(
              map,
              c.lat,
              c.lon,
              c.items.map((i) => ({
                id: i.id,
                label: i.label ?? i.id,
                severity: i.severity,
              })),
              style.expandable ? c.key : undefined
            );
          })
          .addTo(layer);
      });

      return layer;
    };

    jejuLayerRef.current?.remove();
    const jejuLayer = drawClusters(
      jeju,
      declutterClusters(jeju, clusterByPixel(jeju, pois.filter(isJejuPoi), JEJU_CLUSTER_PX)),
      JEJU_MARKERS
    );
    jejuLayer.addTo(jeju);
    jejuLayerRef.current = jejuLayer;

    const mainlandPois = pois.filter((p) => !isJejuPoi(p));

    const draw = () => {
      clusterRef.current?.remove();
      const placed = focus
        ? []
        : declutterClusters(main, clusterByRegion(mainlandPois, anchorsRef.current));
      const layer = drawClusters(main, placed, MAINLAND_MARKERS);
      layer.addTo(main);
      clusterRef.current = layer;
    };

    draw();
    main.on("zoomend", draw);

    return () => {
      main.off("zoomend", draw);
      clusterRef.current?.remove();
      clusterRef.current = null;
      jejuLayerRef.current?.remove();
      jejuLayerRef.current = null;
    };
  }, [pois, selectedId, expanded, focusKey, anchorsReady]);

  return (
    <div
      ref={rootRef}
      className={`relative ${showInset ? "safers-inset-open" : ""} ${zoom < EQUIPMENT_LABEL_ZOOM ? "safers-equipment-compact" : ""} ${className}`}
      style={{ background: SEA_BG }}
    >
      <div
        ref={mainElRef}
        className="safers-silhouette absolute inset-0"
        aria-label="전국 현장 지도"
      />

      {photoSiteKey != null && (
        <AerialPhotoControl
          catalog={catalog}
          dates={photoDates}
          selectedDate={selectedPhotoDate}
          enabled={aerialEnabled}
          onDateChange={(date) => setPickedPhoto({ siteKey: photoSiteKey, date })}
          onEnabledChange={setAerialEnabled}
        />
      )}

      <div
        className={`pointer-events-auto absolute bottom-2 right-2 z-[500] overflow-hidden rounded-lg border border-[#C8CEDA] bg-white shadow-md transition-opacity duration-300 ${
          showInset ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="px-2 pt-1 text-[0.625rem] font-semibold leading-none text-neutral-500">
          제주
        </div>
        <div
          ref={jejuElRef}
          className="safers-silhouette h-[7rem] w-[10rem]"
          aria-label="제주 현장 지도"
        />
      </div>

      <ModelDevicePopover
        selection={deviceSelection}
        selected={shownDevices?.find((d) => d.deviceId === deviceSelection?.id)}
        onClose={() => setDeviceSelection(null)}
      />

      {focus && devices && deviceFilter && onDeviceFilterChange && (
        <ModelDeviceFilters
          devices={devices}
          filter={deviceFilter}
          onChange={onDeviceFilterChange}
        />
      )}

      {focus && deviceNotice && (
        <p
          role="status"
          className="absolute bottom-6 left-2.5 z-[500] rounded bg-white/90 px-2 py-1 text-xs text-neutral-600"
        >
          {deviceNotice}
        </p>
      )}

      {popover && (
        <MapPopover
          state={popover}
          containerWidth={size.w}
          containerHeight={size.h}
          onSitePick={pickFromList}
        />
      )}
    </div>
  );
}
